export const extractOptionString = (opt) => {
  if (opt === null || opt === undefined) return "";
  if (typeof opt === "object") {
    const val =
      opt.label !== undefined && opt.label !== null
        ? opt.label
        : opt.value !== undefined && opt.value !== null
        ? opt.value
        : "";
    return typeof val === "object" ? extractOptionString(val) : String(val).trim();
  }
  return String(opt).trim();
};

export const extractOptionValue = (opt) => {
  if (opt === null || opt === undefined) return "";
  if (typeof opt === "object") {
    const val =
      opt.value !== undefined && opt.value !== null
        ? opt.value
        : opt.label !== undefined && opt.label !== null
        ? opt.label
        : "";
    return typeof val === "object" ? extractOptionValue(val) : String(val).trim();
  }
  return String(opt).trim();
};

export const normalizeComparable = (value) => {
  if (value === null || value === undefined) return "";
  const extracted =
    typeof value === "object" && !Array.isArray(value)
      ? extractOptionValue(value)
      : value;
  return typeof extracted === "string" ? extracted.trim().toLowerCase() : extracted;
};

export const shouldShowSpecialField = (field, sectionValues) => {
  if (!field?.show_when) return true;

  const {
    field: dependencyKey,
    operator = "equals",
    value: expectedValue,
  } = field.show_when;

  if (!dependencyKey) return true;

  const actualValue = sectionValues?.[dependencyKey];

  if (operator === "contains") {
    if (Array.isArray(actualValue)) {
      const normExpected = normalizeComparable(expectedValue);
      return actualValue.some((v) => normalizeComparable(v) === normExpected);
    }
    return false;
  }

  const normActual = normalizeComparable(actualValue);
  const normExpected = normalizeComparable(expectedValue);

  if (operator === "equals" || operator === "==" || !operator) {
    return normActual === normExpected;
  }

  if (operator === "not_equals" || operator === "!=") {
    return normActual !== normExpected;
  }

  return true;
};

export const evaluateSectionShowWhen = (showWhen, sectionValues) => {
  if (!showWhen) return true;

  const dependencyKey = showWhen.field;
  if (!dependencyKey) return true;

  const operator = showWhen.operator || "equals";
  const expectedValue = showWhen.value;
  const actualValue = sectionValues?.[dependencyKey];

  if (operator === "contains") {
    if (Array.isArray(actualValue)) {
      const normExpected = normalizeComparable(expectedValue);
      return actualValue.some((v) => normalizeComparable(v) === normExpected);
    }
    return false;
  }

  const normActual = normalizeComparable(actualValue);
  const normExpected = normalizeComparable(expectedValue);

  if (operator === "equals" || operator === "==" || !operator) {
    return normActual === normExpected;
  }

  if (operator === "not_equals" || operator === "!=") {
    return normActual !== normExpected;
  }

  return true;
};

export const cleanHiddenSpecialFields = (fields, sectionValues) => {
  if (!Array.isArray(fields) || !sectionValues || typeof sectionValues !== "object") {
    return sectionValues;
  }

  const cleaned = { ...sectionValues };
  let changed = false;

  fields.forEach((field) => {
    if (!field || !field.key) return;
    const isVisible = shouldShowSpecialField(field, cleaned);
    if (!isVisible) {
      if (cleaned[field.key] !== undefined) {
        delete cleaned[field.key];
        changed = true;
      }
      if (field.name_key && cleaned[field.name_key] !== undefined) {
        delete cleaned[field.name_key];
        changed = true;
      }
      if (field.signature_key && cleaned[field.signature_key] !== undefined) {
        delete cleaned[field.signature_key];
        changed = true;
      }
    }
  });

  return changed ? cleaned : sectionValues;
};


export const extractAnswerString = (ans) => {
  if (ans === null || ans === undefined) return "";
  if (typeof ans === "object") {
    if (ans.value !== undefined && ans.value !== null) {
      return typeof ans.value === "object"
        ? extractAnswerString(ans.value)
        : String(ans.value).trim();
    }
    if (ans.label !== undefined && ans.label !== null) {
      return typeof ans.label === "object"
        ? extractAnswerString(ans.label)
        : String(ans.label).trim();
    }
    try {
      return JSON.stringify(ans);
    } catch (_) {
      return "";
    }
  }
  return String(ans).trim();
};

export const isClosedPermit = (permit) => {
  if (!permit) return false;
  const status =
    permit.current_status ||
    permit.workflow_summary?.current_status ||
    "";
  return String(status).trim().toLowerCase() === "closed";
};

export const canShowPermitReportDownload = (permit) => {
  if (!permit) return false;
  return isClosedPermit(permit);
};

export const formatStatusLabel = (status) => {
  if (!status) return "-";
  const raw = String(status).trim().toLowerCase();

  switch (raw) {
    case "draft":
      return "Draft";
    case "issue_in_progress":
    case "pending_issue":
      return "Issue In Progress";
    case "issued":
    case "approved":
      return "Issued";
    case "closure_in_progress":
    case "pending_closure":
      return "Closure In Progress";
    case "closed":
      return "Closed";
    case "rejected":
      return "Rejected";
    case "cancelled":
      return "Cancelled";
    case "expired":
      return "Expired";
    case "suspended":
      return "Suspended";
    default:
      return raw
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
};

export const getStatusBadgeClass = (status) => {
  if (!status) return "bg-slate-100 text-slate-700 border-slate-200";
  const raw = String(status).trim().toLowerCase();

  switch (raw) {
    case "draft":
      return "bg-slate-100 text-slate-700 border-slate-200";
    case "issue_in_progress":
    case "pending_issue":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "issued":
    case "approved":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "closure_in_progress":
    case "pending_closure":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "closed":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "rejected":
      return "bg-red-100 text-red-800 border-red-200";
    case "cancelled":
      return "bg-slate-200 text-slate-800 border-slate-300";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const cleanText = (value) => {
  const text = String(value ?? "").trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
};

export const getFormattedPtwNo = (permit) => {
  if (!permit) return "";

  const direct =
    cleanText(permit.ptw_no) ||
    cleanText(permit.ptw_display_no) ||
    cleanText(permit.ptw_number) ||
    cleanText(permit.permit_no) ||
    cleanText(permit.formatted_ptw_no) ||
    cleanText(permit.report_no) ||
    cleanText(permit.safety_report_no) ||
    cleanText(permit?.report_meta?.ptw_no) ||
    cleanText(permit?.report_meta?.permit_no) ||
    cleanText(permit?.form_data?.ptw_no) ||
    cleanText(permit?.form_data?.permit_no);

  if (direct && !/^PTW-\d+$/i.test(direct)) {
    return direct;
  }

  const prefix =
    cleanText(permit?.template_snapshot?.ptw_prefix) ||
    cleanText(permit?.template?.ptw_prefix);

  const sequence =
    cleanText(permit.ptw_sequence) ||
    cleanText(permit.sequence_no) ||
    cleanText(permit.permit_sequence) ||
    cleanText(permit.report_sequence) ||
    (permit.id ? String(permit.id).padStart(5, "0") : "");

  if (prefix && sequence) {
    const cleanPrefix = prefix.replace(/-+$/, "");
    return `${cleanPrefix}-${String(sequence).padStart(5, "0")}`;
  }

  return direct || (permit.id ? `PTW-${permit.id}` : "");
};

export const FIELD_LABELS = {
  location: "Location",
  permit_applicant: "Permit Applicant",
  contractor_name: "Contractor Name",
  scope_of_work: "Type & Scope of Work",
  workmen_count: "Workmen Count",
  // contact_number: "Contact Number",
  provided_ppes: "Provided PPEs",
  ptw_no: "PTW No.",
  template: "Permit Template",
  project_id: "Project",
  organization_id: "Organization",
  checklist_response: "Checklist",
  signature: "Signature",
};

export const getFriendlyFieldLabel = (fieldKey = "") => {
  const key = String(fieldKey || "").trim();
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];

  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "number") return false;

  if (typeof value === "string") {
    return !value.trim();
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    const values = Object.values(value);
    if (values.length === 0) return true;
    return values.every((v) => isEmptyValue(v));
  }

  return false;
};

const extractFirstMessage = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    for (const item of val) {
      const msg = extractFirstMessage(item);
      if (msg) return msg;
    }
    return "";
  }
  if (typeof val === "object") {
    for (const item of Object.values(val)) {
      const msg = extractFirstMessage(item);
      if (msg) return msg;
    }
    return "";
  }
  return String(val);
};

export const normalizeApiErrorMessage = (error) => {
  const data = error?.response?.data || error?.data || error;

  if (!data) {
    return "Something went wrong. Please try again.";
  }

  if (typeof data === "string") {
    if (data.toLowerCase().includes("location")) {
      return "Location is required. Please enter permit location.";
    }
    return data;
  }

  if (data.detail) {
    const msg = extractFirstMessage(data.detail);
    if (msg) {
      if (msg.toLowerCase().includes("location")) {
        return "Location is required. Please enter permit location.";
      }
      return msg;
    }
  }

  if (data.message) {
    const msg = extractFirstMessage(data.message);
    if (msg) {
      if (msg.toLowerCase().includes("location")) {
        return "Location is required. Please enter permit location.";
      }
      return msg;
    }
  }

  if (data.form_data && typeof data.form_data === "object") {
    const formErrors = data.form_data;
    const firstKey = Object.keys(formErrors)[0];

    if (firstKey) {
      const label = getFriendlyFieldLabel(firstKey);
      const rawMessage = extractFirstMessage(formErrors[firstKey]);
      const msg = String(rawMessage || "").trim();

      if (firstKey === "location" || msg.toLowerCase().includes("location")) {
        return "Location is required. Please enter permit location.";
      }

      if (msg.toLowerCase().includes("required")) {
        return `${label} is required. Please fill this field.`;
      }

      return msg ? `${label}: ${msg}` : `${label} is required. Please fill this field.`;
    }
  }

  if (typeof data === "object") {
    const firstKey = Object.keys(data)[0];

    if (firstKey) {
      const label = getFriendlyFieldLabel(firstKey);
      const rawMessage = extractFirstMessage(data[firstKey]);
      const msg = String(rawMessage || "").trim();

      if (firstKey === "location" || msg.toLowerCase().includes("location")) {
        return "Location is required. Please enter permit location.";
      }

      if (msg.toLowerCase().includes("required")) {
        return `${label} is required. Please fill this field.`;
      }

      return msg ? `${label}: ${msg}` : `${label} is required. Please fill this field.`;
    }
  }

  return "Something went wrong. Please check the permit details and try again.";
};

export const validatePermitRequiredFields = (fieldsConfig = [], formData = {}) => {
  if (!Array.isArray(fieldsConfig) || fieldsConfig.length === 0) {
    return "";
  }

  for (const fc of fieldsConfig) {
    const fd = fc?.field_definition || fc;
    const key = fc?.key || fd?.key || fc?.name || fd?.name;

    if (!key) continue;

    const isRequired =
      fc?.required_override ??
      fd?.required_default ??
      fc?.required ??
      fd?.required ??
      fc?.is_required ??
      fd?.is_required ??
      false;

    if (isRequired) {
      const value = formData?.[key];

      if (isEmptyValue(value)) {
        if (key === "location") {
          return "Location is required. Please enter permit location.";
        }

        const label = fc?.label || fd?.label || getFriendlyFieldLabel(key);
        return `${label} is required. Please fill this field.`;
      }
    }
  }

  return "";
};

export const parsePpeValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "object" && item !== null) {
          return item?.label || item?.value || item?.name || "";
        }
        return String(item || "");
      })
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

export const isProvidedPpeField = (field) => {
  if (!field) return false;

  if (typeof field === "string") {
    const key = field.trim().toLowerCase();
    return (
      key === "provided_ppes" ||
      key === "provided_ppe" ||
      key === "ppes" ||
      key === "ppe" ||
      key.includes("provided_ppe") ||
      key.includes("provided ppe")
    );
  }

  const key = String(
    field?.key || field?.field_definition?.key || field?.name || ""
  )
    .trim()
    .toLowerCase();

  const label = String(
    field?.label || field?.field_definition?.label || ""
  )
    .trim()
    .toLowerCase();

  return (
    key === "provided_ppes" ||
    key === "provided_ppe" ||
    key === "ppes" ||
    key === "ppe" ||
    key.includes("provided_ppe") ||
    label.includes("provided ppe") ||
    label.includes("provided ppes") ||
    label.includes("ppes provided") ||
    label.includes("ppe provided")
  );
};

export const formatPermitDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, "0");

  return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
};

/**
 * Returns structured workflow timestamps for a permit list item:
 * - For CLOSED:
 *     { mode: "closed", issuedText: "Issued Date: ...", closedText: "Closed Date: ..." }
 * - For CANCELLED:
 *     { mode: "cancelled", issuedText: "Issued Date: ...", cancelText: "Cancel Date: ..." }
 * - For REJECTED:
 *     { mode: "rejected", issuedText: "Issued Date: ...", rejectedText: "Rejected Date: ..." }
 * - For others (Draft, In Progress, etc.):
 *     { mode: "movement", dateText: "Date & Time: ..." }
 */
export const getPermitWorkflowTimestamps = (item) => {
  if (!item) {
    return {
      startTime: null,
      startTimeDisplay: "—",
      startTimeText: null,
      endTime: null,
      endTimeDisplay: "—",
      endTimeText: null,
      isPending: false,
      isCompleted: false,
      shouldShowTimestamps: false,
      isPendingForClosure: false,
      dateText: "",
    };
  }

  const rawStatus = String(
    item.current_status ||
    item.status ||
    item.workflow_summary?.current_status ||
    ""
  ).trim().toLowerCase();

  const isClosed = rawStatus === "closed";
  const isDraft = rawStatus === "draft";
  const isFinalCancelled =
    rawStatus === "cancelled" || Boolean(item.is_final_cancelled);
  const isFinalRejected =
    rawStatus === "rejected" || Boolean(item.is_final_rejected);
  const isPendingForClosure = rawStatus === "closure_in_progress";

  const actionTs = item.action_timestamps || {};

  // 1. Resolve Start Time (Workflow Started At)
  // Authoritative event: Maker first submits the permit into the workflow.
  // Preserved across all subsequent approval stages, returns, and resubmissions. Drafts remain blank.
  const rawStart =
    item.workflow_started_at ||
    actionTs.workflow_started_at ||
    (!isDraft && (item.submitted_at || actionTs.raised_at));

  const startDisplay =
    (!isDraft && (item.workflow_started_at_display || actionTs.workflow_started_at_display)) ||
    (!isDraft && rawStart ? formatPermitDateTime(rawStart) : "—");

  // 2. Resolve End Time (Workflow Completed At)
  // End Time must be recorded and displayed ONLY after the entire Permit Closure workflow is successfully completed (CLOSED).
  // Remains null in backend and blank in UI throughout active workflow, issuance, and Pending for Closure.
  // Never falls back to issued_at or "Pending".
  const rawEnd = isClosed
    ? (item.workflow_completed_at ||
       actionTs.workflow_completed_at ||
       item.closed_at ||
       actionTs.closed_at)
    : null;

  const isCompleted = Boolean(isClosed && rawEnd);

  const endDisplay = isCompleted
    ? (item.workflow_completed_at_display ||
       actionTs.workflow_completed_at_display ||
       item.closed_at_display ||
       actionTs.closed_at_display ||
       formatPermitDateTime(rawEnd))
    : "—";

  const shouldShowTimestamps = !isDraft && Boolean(rawStart);

  // Also expose separate reliable rejection / cancellation / closure timestamps if available in history
  const rawReject = item.rejected_at || actionTs.rejected_at || item.last_rejected_at;
  const rejectDisplay =
    item.rejected_at_display ||
    actionTs.rejected_at_display ||
    (rawReject ? formatPermitDateTime(rawReject) : null);

  const rawCancel =
    item.cancelled_at ||
    actionTs.cancelled_at ||
    item.latest_cancelled_at;
  const cancelDisplay =
    item.cancelled_at_display ||
    actionTs.cancelled_at_display ||
    (rawCancel ? formatPermitDateTime(rawCancel) : null);

  const rawClosed = item.closed_at || actionTs.closed_at;
  const closedDisplay =
    item.closed_at_display ||
    actionTs.closed_at_display ||
    (rawClosed ? formatPermitDateTime(rawClosed) : null);

  const rawIssued = item.issued_at || actionTs.issued_at;
  const issuedDisplay =
    item.issued_at_display ||
    actionTs.issued_at_display ||
    (rawIssued ? formatPermitDateTime(rawIssued) : "—");

  return {
    startTime: rawStart || null,
    startTimeDisplay: startDisplay,
    startTimeText: (!isDraft && startDisplay !== "—") ? `Start Time: ${startDisplay}` : null,
    endTime: isCompleted ? rawEnd : null,
    endTimeDisplay: endDisplay,
    endTimeText: isCompleted ? `End Time: ${endDisplay}` : null,
    isPending: false,
    isCompleted,
    shouldShowTimestamps,
    isPendingForClosure,
    rejectionTimeDisplay: rejectDisplay,
    cancellationTimeDisplay: cancelDisplay,
    closedTimeDisplay: closedDisplay,
    mode: isClosed ? "closed" : isFinalCancelled ? "cancelled" : isFinalRejected ? "rejected" : "movement",
    issuedText: `Issued Date: ${issuedDisplay}`,
    closedText: `Closed Date: ${closedDisplay || "—"}`,
    cancelText: `Cancel Date: ${cancelDisplay || "—"}`,
    rejectedText: `Rejected Date: ${rejectDisplay || "—"}`,
    dateText: isCompleted
      ? `Start: ${startDisplay} · End: ${endDisplay}`
      : (!isDraft && startDisplay !== "—")
      ? `Start Time: ${startDisplay}`
      : `Start Time: —`,
  };
};

/**
 * Resolves rejection and cancellation counter badges for a permit.
 * Explicitly differentiates:
 * 1. During Approval: "Approval Rejection X of 3"
 * 2. During Closure: "Closure Rejection X of 3"
 * 3. In case of Cancellation: "Cancellation X of 3"
 */
export const getPermitRejectionBadges = (item) => {
  if (!item) return [];
  const badges = [];

  const rawStatus = String(
    item.workflow_summary?.current_status ?? item.current_status ?? ""
  )
    .trim()
    .toLowerCase();

  const issueCount = Number(item.issue_rejection_count || 0);
  const closureCount = Number(item.closure_rejection_count || 0);
  const cancelCount = Number(item.cancel_count || 0);
  const isFinalRejected = Boolean(item.is_final_rejected || issueCount >= 3);
  const isFinalCancelled = Boolean(item.is_final_cancelled || cancelCount >= 3);

  // 1. Rejection during Approval
  if (issueCount > 0 || (rawStatus === "rejected" && closureCount === 0)) {
    const count = issueCount > 0 ? issueCount : 1;
    badges.push({
      type: "approval_rejection",
      phase: "approval",
      label: `Approval Rejection ${count} of 3`,
      count,
      limit: 3,
      isLimitReached: isFinalRejected || count >= 3,
      className: isFinalRejected
        ? "border-red-200 bg-red-50 text-red-700"
        : "border-orange-200 bg-orange-50 text-orange-700",
    });
  }

  // 2. Rejection during Closure
  if (closureCount > 0 || (rawStatus === "rejected" && closureCount > 0)) {
    const count = closureCount > 0 ? closureCount : 1;
    badges.push({
      type: "closure_rejection",
      phase: "closure",
      label: `Closure Rejection ${count} of 3`,
      count,
      limit: 3,
      isLimitReached: count >= 3,
      className: "border-amber-200 bg-amber-50 text-amber-700",
    });
  }

  // 3. In case of Cancellation
  if (cancelCount > 0 || rawStatus === "cancelled") {
    const count = cancelCount > 0 ? cancelCount : 1;
    badges.push({
      type: "cancellation",
      phase: "cancellation",
      label: `Cancellation ${count} of 3`,
      count,
      limit: 3,
      isLimitReached: isFinalCancelled || count >= 3,
      className: "border-red-200 bg-red-50 text-red-700",
    });
  }

  return badges;
};

export const isClosureRejection = (permit) => {
  if (!permit) return false;
  const rawStatus = String(
    permit.workflow_summary?.current_status ?? permit.current_status ?? ""
  )
    .trim()
    .toLowerCase();
  if (rawStatus !== "rejected") return false;

  if (Number(permit.closure_rejection_count || 0) > 0) return true;

  const logs = Array.isArray(permit.workflow_logs) ? permit.workflow_logs : [];
  const lastReject = [...logs].reverse().find(
    (l) => String(l?.action || "").toLowerCase() === "reject"
  );
  if (lastReject) {
    const from = String(lastReject.status_from || "").toLowerCase();
    const stage = String(lastReject.from_stage || "").toLowerCase();
    if (from.includes("closure") || stage.includes("closure")) return true;
  }

  return false;
};

export const getAnswerBadgeClasses = (answer) => {
  const str = extractAnswerString(answer);
  const normalized = String(str || "").trim().toLowerCase();

  if (normalized === "yes") {
    return "border-green-500 bg-green-50 text-green-700";
  }
  if (normalized === "no") {
    return "border-red-500 bg-red-50 text-red-700";
  }
  if (
    normalized === "n/a" ||
    normalized === "na" ||
    normalized === "not applicable" ||
    normalized === "not_applicable"
  ) {
    return "border-yellow-500 bg-yellow-50 text-yellow-700";
  }
  return "border-slate-300 bg-slate-50 text-slate-700";
};

export const isTemperatureRow = (row) => {
  if (!row) return false;
  const candidates = [
    row.key,
    row.gas,
    row.gas_test_result,
    row.label,
    row.name,
    row.test_name,
    row.parameter,
    row.item,
  ];
  return candidates.some((c) => {
    if (typeof c !== "string") return false;
    const lower = c.trim().toLowerCase();
    return (
      lower.includes("temperature") ||
      lower === "temp" ||
      lower.startsWith("temp_") ||
      lower.startsWith("temperature_")
    );
  });
};

export const isPermissibleLimitColumn = (col, section = null) => {
  if (!col) return false;

  if (section && Array.isArray(section.columns)) {
    const isReferencedAsLimit = section.columns.some(
      (c) => c?.validation?.permissible_field === col.key
    );
    if (isReferencedAsLimit) return true;
  }

  const candidates = [col.key, col.label, col.name, col.field];
  return candidates.some((c) => {
    if (typeof c !== "string") return false;
    const lower = c.trim().toLowerCase();
    return (
      lower.includes("permissible") ||
      lower.includes("without_ba") ||
      lower.includes("without ba") ||
      lower === "limit"
    );
  });
};

const isExplicitTruthy = (val) => {
  if (val === true) return true;
  if (typeof val === "string" && ["true", "1", "yes"].includes(val.trim().toLowerCase())) return true;
  if (val === 1) return true;
  return false;
};

const isExplicitFalsey = (val) => {
  if (val === false) return true;
  if (typeof val === "string" && ["false", "0", "no"].includes(val.trim().toLowerCase())) return true;
  if (val === 0) return true;
  return false;
};

const resolveExplicitState = (source) => {
  if (!source || typeof source !== "object") return null;

  if (isExplicitTruthy(source.disabled) || isExplicitTruthy(source.is_disabled)) return false;
  if (isExplicitFalsey(source.enabled) || isExplicitFalsey(source.is_enabled)) return false;

  if (isExplicitFalsey(source.disabled) || isExplicitFalsey(source.is_disabled)) return true;
  if (isExplicitTruthy(source.enabled) || isExplicitTruthy(source.is_enabled)) return true;

  const status = typeof source.status === "string" ? source.status.trim().toLowerCase() : "";
  if (status === "disabled" || status === "inactive") return false;
  if (status === "enabled" || status === "active") return true;

  if (source.active === false) return false;
  if (source.active === true) return true;

  return null;
};

export const isSpecialRowEnabled = (row, section = null, formData = null) => {
  if (!row) return false;
  const rowKey = String(row.key || "").trim();
  const secKey = section?.key ? String(section.key).trim() : null;

  const sectionData = secKey && formData && typeof formData === "object" ? formData[secKey] : null;
  const submittedRow = sectionData?.rows?.[rowKey] || null;

  // 1. Submitted row state
  if (submittedRow) {
    const res = resolveExplicitState(submittedRow);
    if (res !== null) return res;
  }

  // 2. Submitted section state
  if (sectionData) {
    const disabledRows = sectionData.disabled_rows || sectionData.disabled_row_keys;
    if (Array.isArray(disabledRows)) {
      const disabledSet = new Set(disabledRows.map((k) => String(k).trim()));
      if (disabledSet.has(rowKey)) return false;
    }
    const enabledRows = sectionData.enabled_rows || sectionData.enabled_row_keys;
    if (Array.isArray(enabledRows)) {
      const enabledSet = new Set(enabledRows.map((k) => String(k).trim()));
      if (!enabledSet.has(rowKey)) return false;
    }
  }

  // 3. Template row configuration
  const rowRes = resolveExplicitState(row);
  if (rowRes !== null) return rowRes;

  // 4. Template section configuration
  if (section) {
    const secDisabledRows = section.disabled_rows || section.disabled_row_keys;
    if (Array.isArray(secDisabledRows)) {
      const secDisabledSet = new Set(secDisabledRows.map((k) => String(k).trim()));
      if (secDisabledSet.has(rowKey)) return false;
    }
    const secEnabledRows = section.enabled_rows || section.enabled_row_keys;
    if (Array.isArray(secEnabledRows)) {
      const secEnabledSet = new Set(secEnabledRows.map((k) => String(k).trim()));
      if (!secEnabledSet.has(rowKey)) return false;
    }
  }

  // 5. Default = ENABLED
  return true;
};

export const isSpecialCellDisabled = (row, col, section = null, formData = null) => {
  if (!col) return false;
  // If the entire row is disabled: cell = disabled
  if (!isSpecialRowEnabled(row, section, formData)) {
    return true;
  }

  const rowKey = String(row?.key || "").trim();
  const colKey = String(col?.key || "").trim();
  const secKey = section?.key ? String(section.key).trim() : null;

  const sectionData = secKey && formData && typeof formData === "object" ? formData[secKey] : null;
  const submittedRow = sectionData?.rows?.[rowKey] || null;

  // Check submitted row cell-level disabled state
  if (submittedRow) {
    if (isExplicitTruthy(submittedRow[`${colKey}_disabled`])) return true;
    if (isExplicitFalsey(submittedRow[`${colKey}_enabled`])) return true;
    const subDisabledCols = submittedRow.disabled_columns || submittedRow.disabled_fields;
    if (Array.isArray(subDisabledCols)) {
      if (subDisabledCols.map((c) => String(c).trim()).includes(colKey)) return true;
    }
  }

  // Check template row configuration
  if (row) {
    if (isExplicitTruthy(row[`${colKey}_disabled`])) return true;
    if (isExplicitFalsey(row[`${colKey}_enabled`])) return true;
    const rowDisabledCols = row.disabled_columns || row.disabled_fields;
    if (Array.isArray(rowDisabledCols)) {
      if (rowDisabledCols.map((c) => String(c).trim()).includes(colKey)) return true;
    }
  }

  // Check column-level row restrictions
  if (col) {
    if (Array.isArray(col.disabled_row_keys)) {
      if (col.disabled_row_keys.map((k) => String(k).trim()).includes(rowKey)) return true;
    }
    if (Array.isArray(col.editable_row_keys) && col.editable_row_keys.length > 0) {
      if (!col.editable_row_keys.map((k) => String(k).trim()).includes(rowKey)) return true;
    }
  }

  return false;
};

export const isTemperaturePermissibleLimitCell = (
  row,
  col,
  section = null,
  formData = null
) => {
  // Backward compatibility wrapper delegating to dynamic isSpecialCellDisabled
  return isSpecialCellDisabled(row, col, section, formData);
};

export const DEFAULT_MAKER_DISCLAIMER =
  "I confirm that I have been given charge of the above mentioned work and I will take all necessary precaution to avoid danger to the workers engaged at the above site as well as property. I will abide by the recommendations of the safety engineer and implement them and will assign jobs to only trained personnel";

export const getPermitMakerDisclaimer = (permit) => {
  if (!permit) return DEFAULT_MAKER_DISCLAIMER;

  // 1. Check if maker signature has stored consent_data text
  const logs = Array.isArray(permit?.workflow_logs) ? permit.workflow_logs : [];
  for (const log of logs) {
    const sigs = Array.isArray(log?.signatures)
      ? log.signatures
      : Array.isArray(log?.action_signatures)
      ? log.action_signatures
      : [];
    for (const sig of sigs) {
      if (sig?.consent_data?.text && String(sig.consent_data.text).trim()) {
        return String(sig.consent_data.text).trim();
      }
    }
  }

  const directSigs = Array.isArray(permit?.action_signatures) ? permit.action_signatures : [];
  for (const sig of directSigs) {
    if (sig?.consent_data?.text && String(sig.consent_data.text).trim()) {
      return String(sig.consent_data.text).trim();
    }
  }

  // 2. Check template_snapshot signature_boxes
  const boxes =
    permit?.template_snapshot?.signature_boxes ||
    permit?.signature_boxes ||
    [];
  const makerBox = boxes.find(
    (b) =>
      b.key === "issue_maker_signature" ||
      b.signer_type === "creator" ||
      b.layout_config?.signer_type === "creator" ||
      b.action === "submit"
  );
  if (makerBox?.layout_config?.consent_text && String(makerBox.layout_config.consent_text).trim()) {
    return String(makerBox.layout_config.consent_text).trim();
  }

  // 3. Check current_signature_consent text
  if (permit?.current_signature_consent?.text && String(permit.current_signature_consent.text).trim()) {
    return String(permit.current_signature_consent.text).trim();
  }

  // 4. Default fallback
  return DEFAULT_MAKER_DISCLAIMER;
};

export const DEFAULT_CONTRACTOR_EHS_DECLARATION =
  "Identified EHS measure / isolation actions : The precaution and safe condition mentioned in checkpoints have been verified & satisfactory and allowed to work.";

export const isContractorEhsGroupOrStage = (permit, userGroups = []) => {
  if (!permit) return false;

  const code = String(
    permit?.workflow_summary?.current_group_code ??
    permit?.current_assigned_group_code ??
    ""
  ).trim().toUpperCase();

  const name = String(
    permit?.workflow_summary?.current_group_name ??
    permit?.current_assigned_group_name ??
    ""
  ).trim().toLowerCase();

  if (
    code === "CONTRACTOR_EHS" ||
    code.includes("CONTRACTOR_EHS") ||
    code === "CONTRACTOR_SAFETY_SUPERVISOR" ||
    code === "CONTRACTOR_SAFETY_OFFICER" ||
    code === "CONTRACTOR_SAFETY"
  ) {
    return true;
  }

  if (name.includes("contractor") && (name.includes("ehs") || name.includes("safety") || name.includes("hse"))) {
    return true;
  }

  const groupId =
    permit?.workflow_summary?.current_group_id ??
    permit?.current_assigned_group_id;

  if (groupId && Array.isArray(userGroups)) {
    const group = userGroups.find((g) => String(g.id) === String(groupId));
    if (group) {
      const gCode = String(group.code || "").trim().toUpperCase();
      const gName = String(group.name || "").trim().toLowerCase();
      if (
        gCode === "CONTRACTOR_EHS" ||
        gCode.includes("CONTRACTOR_EHS") ||
        gCode.includes("CONTRACTOR_SAFETY")
      ) {
        return true;
      }
      if (gName.includes("contractor") && (gName.includes("ehs") || gName.includes("safety") || gName.includes("hse"))) {
        return true;
      }
    }
  }

  return false;
};

export const getCheckerConsentConfig = (permit, userGroups = []) => {
  // Authoritative server calculation from permit serializer
  const serverConsent = permit?.current_signature_consent;
  if (serverConsent && typeof serverConsent.enabled === "boolean") {
    if (!serverConsent.enabled) {
      return {
        enabled: false,
        text: "",
        signature_box_key: serverConsent.signature_box_key || "",
      };
    }
    return {
      enabled: true,
      text: serverConsent.text || DEFAULT_CONTRACTOR_EHS_DECLARATION,
      signature_box_key: serverConsent.signature_box_key || "",
    };
  }

  // Fallback if server consent is missing: only Contractor EHS gets declaration
  if (isContractorEhsGroupOrStage(permit, userGroups)) {
    return {
      enabled: true,
      text: DEFAULT_CONTRACTOR_EHS_DECLARATION,
      signature_box_key: "issue_checker_signature1",
    };
  }

  return {
    enabled: false,
    text: "",
    signature_box_key: "",
  };
};

export {
  parseStartOfDay,
  parseEndOfDay,
  getPermitCanonicalDate,
  matchesPermitDateRange,
  getPermitContractorClassification,
  matchesPermitContractor,
  buildContractorFilterOptions,
  filterPermitItem,
} from "./permitFilterHelpers";

